import React, { useEffect, useMemo, useState } from "react";
import { Ic, DomainTag } from "../components/UI";
import {
  formatContributionCurrency,
  getActivityContributionSummary,
  getActivityContributionTotal,
  getActivityContributionValueTotal,
  sumActivitiesContributionTotal,
  sumActivitiesContributionValueTotal,
} from "../utils/activityContributions";

/* ═══════════════════════════════════════════
   INJECT STYLES
   ═══════════════════════════════════════════ */

if (typeof document !== "undefined") {
  const id = "dashboard-page-styles";
  if (!document.getElementById(id)) {
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      @keyframes dashFadeIn {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes barGrow {
        from { height: 0; }
      }
      .dash-stat-card { transition: transform .15s ease, box-shadow .15s ease; }
      .dash-stat-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,.07); }
      .dash-bar-col { transition: width .6s cubic-bezier(.4,0,.2,1); }
      .dash-bar-row { transition: background .1s ease; border-radius: 6px; }
      .dash-bar-row:hover { background: #F8F8FC; }
      .dash-bar-row:hover .dash-bar-col { filter: brightness(1.08); }
      .dash-leader-row { transition: background .1s ease; }
      .dash-leader-row:hover { background: #F8F8FC !important; }
      .dash-recent-row { transition: background .1s ease; }
      .dash-recent-row:hover { background: #F8F8FC !important; }
    `;
    document.head.appendChild(s);
  }
}

/* ═══════════════════════════════════════════
   CROWN ICON
   ═══════════════════════════════════════════ */

function CrownIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 8.5 8 12l4-6 4 6 5-3.5-2 11H5z" />
    </svg>
  );
}

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

export default function DashboardPage({ volunteers, domains, activities, beneficiaries = [], onViewVolunteer, isMobile = false, coordinators = [] }) {
  const [domainPage, setDomainPage] = useState(0);
  const [selectedMainDomainId, setSelectedMainDomainId] = useState("");
  const [selectedSubdomainGroupName, setSelectedSubdomainGroupName] = useState("");
  const [rangeType, setRangeType] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const now = new Date();
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const weekStart = new Date(dayStart); weekStart.setDate(weekStart.getDate() - 6);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const parseActivityDate = (iso) => {
    if (!iso) return null;
    const d = new Date(`${iso}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const selectedRange = useMemo(() => {
    if (rangeType === "today") return { from: dayStart, to: dayEnd, label: "היום" };
    if (rangeType === "week") return { from: weekStart, to: dayEnd, label: "השבוע" };
    if (rangeType === "month") return { from: monthStart, to: dayEnd, label: "החודש" };
    if (rangeType === "custom" && customFrom && customTo) {
      const from = new Date(`${customFrom}T00:00:00`);
      const to = new Date(`${customTo}T23:59:59`);
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && from <= to) {
        return { from, to, label: `${customFrom} עד ${customTo}` };
      }
    }
    return { from: monthStart, to: dayEnd, label: "החודש" };
  }, [rangeType, customFrom, customTo, dayStart, dayEnd, weekStart, monthStart]);

  const filteredActivities = useMemo(() => (
    activities.filter((a) => {
      const d = parseActivityDate(a.date);
      if (!d) return false;
      return d >= selectedRange.from && d <= selectedRange.to;
    })
  ), [activities, selectedRange]);

  const rangeHours = filteredActivities.reduce((s, a) => s + Number(a.duration), 0);
  const rangeContributions = sumActivitiesContributionTotal(filteredActivities);
  const rangeContributionValue = sumActivitiesContributionValueTotal(filteredActivities);
  const rangeActivitiesCount = filteredActivities.length;
  const activeVols = volunteers.filter(v => v.status === "active").length;
  const activeDomains = domains.filter(d => d.active).length;

  const recent = [...filteredActivities].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 6);

  const activeDomainsList = useMemo(
    () => domains.filter((d) => d.active !== false),
    [domains]
  );
  const domainById = useMemo(() => {
    const m = {};
    domains.forEach((d) => { m[d.id] = d; });
    return m;
  }, [domains]);
  const isMainDomain = (d) => d?.isMainCategory !== false;
  const resolveMainDomainId = (domainId) => {
    const current = domainById[domainId];
    if (!current) return "";
    if (isMainDomain(current) || !current.parentDomainId) return current.id;
    const parent = domainById[current.parentDomainId];
    return parent ? parent.id : current.id;
  };

  const mainDomains = useMemo(
    () => activeDomainsList.filter((d) => isMainDomain(d)),
    [activeDomainsList]
  );
  const hoursByDomainId = useMemo(() => (
    filteredActivities.reduce((acc, a) => {
      const domainId = (a.domainId || "").trim();
      if (!domainId) return acc;
      acc[domainId] = (acc[domainId] || 0) + Number(a.duration || 0);
      return acc;
    }, {})
  ), [filteredActivities]);
  const mainDomainHours = useMemo(
    () => mainDomains.map((d) => ({
      ...d,
      hours: Object.entries(hoursByDomainId).reduce((s, [domainId, hours]) => (
        resolveMainDomainId(domainId) === d.id ? s + Number(hours || 0) : s
      ), 0),
    })).sort((a, b) => b.hours - a.hours),
    [mainDomains, hoursByDomainId]
  );

  const selectedMainDomain = domains.find((d) => d.id === selectedMainDomainId) || null;
  const selectedMainChildren = useMemo(
    () => activeDomainsList.filter((d) => !isMainDomain(d) && (d.parentDomainId || "") === selectedMainDomainId),
    [activeDomainsList, selectedMainDomainId]
  );
  const childDomainGroupHours = useMemo(() => {
    if (!selectedMainDomainId) return [];
    const groupsMap = {};
    selectedMainChildren.forEach((child) => {
      const groupName = (child.name || "").trim() || "ללא שם";
      const key = groupName.toLowerCase();
      if (!groupsMap[key]) {
        groupsMap[key] = {
          id: `${selectedMainDomainId}__group__${key}`,
          kind: "subdomain_group",
          name: groupName,
          color: child.color || selectedMainDomain?.color || "#64748B",
          hours: 0,
          itemsCount: 0,
        };
      }
      groupsMap[key].itemsCount += 1;
      groupsMap[key].hours += Number(hoursByDomainId[child.id] || 0);
    });
    const rows = Object.values(groupsMap);
    const directOnMain = Number(hoursByDomainId[selectedMainDomainId] || 0);
    if (directOnMain > 0 || rows.length === 0) {
      rows.unshift({
        id: `${selectedMainDomainId}__direct`,
        kind: "main_direct",
        name: "ללא תחום משנה",
        color: selectedMainDomain?.color || "#64748B",
        hours: directOnMain,
      });
    }
    return rows.sort((a, b) => b.hours - a.hours);
  }, [selectedMainDomainId, selectedMainChildren, hoursByDomainId, selectedMainDomain?.color]);

  const cityLevelHours = useMemo(() => {
    if (!selectedMainDomainId || !selectedSubdomainGroupName) return [];
    return selectedMainChildren
      .filter((d) => (d.name || "").trim() === selectedSubdomainGroupName)
      .map((d) => ({
        ...d,
        kind: "city_level",
        hours: Number(hoursByDomainId[d.id] || 0),
      }))
      .sort((a, b) => {
        const diff = Number(b.hours || 0) - Number(a.hours || 0);
        if (diff !== 0) return diff;
        return ((a.city || "").trim() || "ללא עיר").localeCompare(((b.city || "").trim() || "ללא עיר"), "he");
      });
  }, [selectedMainDomainId, selectedSubdomainGroupName, selectedMainChildren, hoursByDomainId]);

  const chartLevel = !selectedMainDomainId ? "main" : (selectedSubdomainGroupName ? "city" : "subdomain");
  const chartData = chartLevel === "main" ? mainDomainHours : (chartLevel === "subdomain" ? childDomainGroupHours : cityLevelHours);
  const chartLabel = (d) => {
    if (!d) return "";
    if (chartLevel === "city") return (d.city || "").trim() || "ללא עיר";
    if (chartLevel === "subdomain") return d.name || "";
    const city = (d.city || "").trim();
    return city ? `${d.name} (${city})` : d.name;
  };
  const maxD = Math.max(...chartData.map(d => d.hours), 1);
  const domainPageSize = isMobile ? 4 : 7;
  const domainPagesCount = Math.max(1, Math.ceil(chartData.length / domainPageSize));
  const visibleDomains = useMemo(
    () => chartData.slice(domainPage * domainPageSize, (domainPage + 1) * domainPageSize),
    [chartData, domainPage, domainPageSize]
  );
  useEffect(() => {
    if (domainPage > domainPagesCount - 1) setDomainPage(Math.max(0, domainPagesCount - 1));
  }, [domainPage, domainPagesCount]);
  useEffect(() => {
    setDomainPage(0);
  }, [selectedMainDomainId, selectedSubdomainGroupName]);
  useEffect(() => {
    if (selectedMainDomainId && !domains.some((d) => d.id === selectedMainDomainId)) {
      setSelectedMainDomainId("");
      setSelectedSubdomainGroupName("");
    }
  }, [domains, selectedMainDomainId]);
  useEffect(() => {
    if (!selectedSubdomainGroupName) return;
    const exists = selectedMainChildren.some((d) => (d.name || "").trim() === selectedSubdomainGroupName);
    if (!exists) setSelectedSubdomainGroupName("");
  }, [selectedSubdomainGroupName, selectedMainChildren]);

  const topVols = volunteers.filter(v => v.status === "active").map(v => ({
    ...v,
    hours: filteredActivities.filter(a => a.volunteerId === v.id).reduce((s, a) => s + Number(a.duration), 0),
  })).sort((a, b) => b.hours - a.hours).slice(0, 5);

  const volunteerById = volunteers.reduce((acc, v) => { acc[v.id] = v; return acc; }, {});
  const coordinatorNameById = coordinators.reduce((acc, c) => { if (c?.id) acc[c.id] = c.name || c.email || c.id; return acc; }, {});

  const coordinatorLeaderboard = Object.values(
    filteredActivities
      .filter(a => (a.status || "pending") === "matched")
      .reduce((acc, a) => {
        const vol = volunteerById[a.volunteerId];
        const coordinatorId = (vol?.coordinatorId || "").trim();
        if (!coordinatorId) return acc;
        if (!acc[coordinatorId]) {
          acc[coordinatorId] = { id: coordinatorId, label: coordinatorNameById[coordinatorId] || coordinatorId, hours: 0, count: 0 };
        }
        acc[coordinatorId].hours += Number(a.duration || 0);
        acc[coordinatorId].count += 1;
        return acc;
      }, {})
  ).sort((a, b) => b.hours - a.hours).slice(0, 5);

  const domainMap = {};
  domains.forEach(d => { domainMap[d.id] = d; });
  const volMap = {};
  volunteers.forEach(v => { volMap[v.id] = v; });
  const normalizeCity = (city) => (city || "").toString().trim();
  const citySummaryRows = useMemo(() => {
    const volunteerCounts = {};
    volunteers.forEach((v) => {
      const city = normalizeCity(v.city);
      if (!city) return;
      volunteerCounts[city] = (volunteerCounts[city] || 0) + 1;
    });
    const beneficiaryCounts = {};
    beneficiaries.forEach((b) => {
      const city = normalizeCity(b.city);
      if (!city) return;
      beneficiaryCounts[city] = (beneficiaryCounts[city] || 0) + 1;
    });
    const cities = new Set([...Object.keys(volunteerCounts), ...Object.keys(beneficiaryCounts)]);
    return Array.from(cities)
      .map((city) => ({
        city,
        volunteersCount: volunteerCounts[city] || 0,
        beneficiariesCount: beneficiaryCounts[city] || 0,
      }))
      .sort((a, b) => (b.volunteersCount + b.beneficiariesCount) - (a.volunteersCount + a.beneficiariesCount));
  }, [volunteers, beneficiaries]);
  const topVolunteerCity = citySummaryRows.slice().sort((a, b) => b.volunteersCount - a.volunteersCount)[0] || null;
  const topBeneficiaryCity = citySummaryRows.slice().sort((a, b) => b.beneficiariesCount - a.beneficiariesCount)[0] || null;

  /* Medal colors */
  const medal = (i) => {
    if (i === 0) return { bg: "#FEF3C7", color: "#B45309", ring: "#FDE68A" };
    if (i === 1) return { bg: "#F1F5F9", color: "#64748B", ring: "#E2E8F0" };
    if (i === 2) return { bg: "#FEF0E1", color: "#C2410C", ring: "#FDBA74" };
    return { bg: "#F5F5F7", color: "#8E8EA0", ring: "#E8E8ED" };
  };

  /* Stat card config */
  const stats = [
    { accent: "#C41E3A", bg: "#FFF1F3", icon: <Ic.Clock />, value: rangeHours.toFixed(1), label: `שעות (${selectedRange.label})` },
    { accent: "#2563EB", bg: "#EFF6FF", icon: <Ic.Calendar />, value: rangeActivitiesCount, label: `פעילויות (${selectedRange.label})` },
    { accent: "#C2410C", bg: "#FFF7ED", icon: <Ic.Grid />, value: rangeContributions, label: `תרומות (${selectedRange.label})` },
    { accent: "#0F766E", bg: "#ECFEFF", icon: <Ic.Chart />, value: formatContributionCurrency(rangeContributionValue) || "—", label: `שווי (${selectedRange.label})` },
    { accent: "#059669", bg: "#ECFDF5", icon: <Ic.Users />, value: activeVols, label: "מתנדבים פעילים" },
    { accent: "#D97706", bg: "#FFFBEB", icon: <Ic.Grid />, value: activeDomains, label: "תחומי פעילות" },
  ];

  return (
    <div style={{ fontFamily: "'Heebo', 'Assistant', sans-serif", direction: "rtl" }}>
      <div style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #E8E8ED",
        padding: "12px 14px",
        marginBottom: 14,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 8,
      }}>
        {[
          { id: "today", label: "היום" },
          { id: "week", label: "השבוע" },
          { id: "month", label: "החודש" },
          { id: "custom", label: "טווח ידני" },
        ].map((opt) => {
          const active = rangeType === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => setRangeType(opt.id)}
              style={{
                border: `1px solid ${active ? "#C41E3A" : "#E2E8F0"}`,
                background: active ? "#FFF1F3" : "#fff",
                color: active ? "#C41E3A" : "#334155",
                borderRadius: 999,
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {opt.label}
            </button>
          );
        })}
        {rangeType === "custom" && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 8px", fontFamily: "inherit" }} />
            <span style={{ fontSize: 12, color: "#64748B" }}>עד</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 8px", fontFamily: "inherit" }} />
          </div>
        )}
      </div>

      {/* ════════ Stat cards ════════ */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(6, 1fr)",
        gap: 12,
        marginBottom: 20,
      }}>
        {stats.map((s, i) => (
          <div
            key={i}
            className="dash-stat-card"
            style={{
              background: "#fff",
              borderRadius: 14,
              padding: isMobile ? 14 : 18,
              border: "1px solid #E8E8ED",
              borderRight: `4px solid ${s.accent}`,
              position: "relative",
              overflow: "hidden",
              animation: `dashFadeIn .3s ease ${i * 0.05}s both`,
            }}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 10,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: s.bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: s.accent, fontSize: 18,
              }}>{s.icon}</div>
            </div>
            <div style={{
              fontFamily: "Assistant", fontSize: isMobile ? 24 : 30,
              fontWeight: 800, color: "#1A1A2E", lineHeight: 1,
              marginBottom: 3,
            }}>{s.value}</div>
            <div style={{ fontSize: 12, color: "#8E8EA0", fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ════════ Two-column: Chart + Leaderboards ════════ */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "3fr 2fr",
        gap: 14,
        marginBottom: 20,
      }}>

        {/* ──── Horizontal bar chart ──── */}
        <div style={{
          background: "#fff", borderRadius: 14,
          border: "1px solid #E8E8ED", padding: 18,
          boxShadow: "0 1px 4px rgba(0,0,0,.03)",
          animation: "dashFadeIn .3s ease .1s both",
        }}>
          <div style={{
            fontSize: 15, fontWeight: 700, fontFamily: "Assistant",
            color: "#1A1A2E", marginBottom: 14,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          }}>
            <span>
              {chartLevel === "main" && "שעות לפי תחום (ראשי)"}
              {chartLevel === "subdomain" && `שעות לפי תת תחום: ${selectedMainDomain?.name || ""}`}
              {chartLevel === "city" && `שעות לפי עיר: ${selectedMainDomain?.name || ""} / ${selectedSubdomainGroupName || ""}`}
            </span>
            {chartLevel !== "main" && (
              <button
                onClick={() => {
                  if (chartLevel === "city") {
                    setSelectedSubdomainGroupName("");
                    return;
                  }
                  setSelectedSubdomainGroupName("");
                  setSelectedMainDomainId("");
                }}
                style={{
                  border: "1px solid #E2E8F0",
                  background: "#fff",
                  borderRadius: 999,
                  padding: "5px 10px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {chartLevel === "city" ? "חזרה לתת־תחומים" : "חזרה לראשיים"}
              </button>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: isMobile ? 250 : 300 }}>
            {visibleDomains.map((d) => {
              const pct = maxD > 0 ? (d.hours / maxD) * 100 : 0;
              const canDrillDown = (
                (chartLevel === "main") ||
                (chartLevel === "subdomain" && d.kind === "subdomain_group")
              );
              return (
                <div
                  key={d.id}
                  className="dash-bar-row"
                  onClick={canDrillDown ? () => {
                    if (chartLevel === "main") {
                      setSelectedMainDomainId(d.id);
                      setSelectedSubdomainGroupName("");
                      return;
                    }
                    if (chartLevel === "subdomain" && d.kind === "subdomain_group") {
                      setSelectedSubdomainGroupName(d.name || "");
                    }
                  } : undefined}
                  style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "4px 0",
                  cursor: canDrillDown ? "pointer" : "default",
                }}>
                  {/* Color dot + name */}
                  <div style={{
                    width: 120, flexShrink: 0,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: 3,
                      background: chartLevel === "main" ? d.color : (d.color || "#64748B"), flexShrink: 0,
                    }} />
                    <span style={{
                      fontSize: 12, color: "#2D2D3F", fontWeight: 500,
                      whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis",
                    }} title={chartLabel(d)}>{chartLabel(d)}</span>
                  </div>

                  {/* Bar */}
                  <div style={{
                    flex: 1, height: 22, background: "#F5F5F7",
                    borderRadius: 6, overflow: "hidden",
                    position: "relative",
                  }}>
                    <div
                      className="dash-bar-col"
                      style={{
                        height: "100%",
                        borderRadius: 6,
                        background: chartLevel === "main"
                          ? `linear-gradient(90deg, ${d.color}CC, ${d.color})`
                          : `linear-gradient(90deg, ${(d.color || "#64748B")}CC, ${d.color || "#64748B"})`,
                        width: `${Math.max(pct, d.hours > 0 ? 3 : 0)}%`,
                        transition: "width .6s cubic-bezier(.4,0,.2,1)",
                      }}
                    />
                  </div>

                  {/* Value */}
                  <span style={{
                    fontSize: 13, fontWeight: 800,
                    color: d.hours > 0 ? (chartLevel === "main" ? d.color : "#475569") : "#B0B0C0",
                    fontFamily: "Assistant",
                    width: 36, textAlign: "left", flexShrink: 0,
                  }}>{d.hours.toFixed(0)}</span>
                </div>
              );
            })}
            {chartData.length === 0 && (
              <div style={{ textAlign: "center", color: "#A3A3B5", fontSize: 13, padding: 24 }}>
                אין נתונים
              </div>
            )}
          </div>
          {chartData.length > domainPageSize && (
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <button
                onClick={() => setDomainPage((p) => Math.min(domainPagesCount - 1, p + 1))}
                disabled={domainPage >= domainPagesCount - 1}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  border: "1px solid #E5E7EB",
                  background: domainPage >= domainPagesCount - 1 ? "#F3F4F6" : "white",
                  color: domainPage >= domainPagesCount - 1 ? "#9CA3AF" : "#374151",
                  cursor: domainPage >= domainPagesCount - 1 ? "default" : "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                }}
                aria-label="הבא"
              >
                ‹
              </button>
              <span style={{ fontSize: 12, color: "#6B7280", minWidth: 56, textAlign: "center" }}>
                {domainPage + 1}/{domainPagesCount}
              </span>
              <button
                onClick={() => setDomainPage((p) => Math.max(0, p - 1))}
                disabled={domainPage <= 0}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  border: "1px solid #E5E7EB",
                  background: domainPage <= 0 ? "#F3F4F6" : "white",
                  color: domainPage <= 0 ? "#9CA3AF" : "#374151",
                  cursor: domainPage <= 0 ? "default" : "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                }}
                aria-label="הקודם"
              >
                ›
              </button>
            </div>
          )}
        </div>

        {/* ──── Top volunteers ──── */}
        <div style={{
          background: "#fff", borderRadius: 14,
          border: "1px solid #E8E8ED", padding: 18,
          boxShadow: "0 1px 4px rgba(0,0,0,.03)",
          animation: "dashFadeIn .3s ease .15s both",
        }}>
          <div style={{
            fontSize: 15, fontWeight: 700, fontFamily: "Assistant",
            color: "#1A1A2E", marginBottom: 14,
          }}>מתנדבים מובילים</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topVols.map((v, i) => {
              const m = medal(i);
              const barW = topVols[0]?.hours ? (v.hours / topVols[0].hours) * 100 : 0;
              return (
                <div
                  key={v.id}
                  className="dash-leader-row"
                  onClick={() => onViewVolunteer && onViewVolunteer(v.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: 10,
                    cursor: onViewVolunteer ? "pointer" : "default",
                  }}
                >
                  {/* Rank */}
                  <span style={{
                    width: 24, height: 24, borderRadius: "50%",
                    background: m.bg, border: `1.5px solid ${m.ring}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, color: m.color,
                    flexShrink: 0,
                  }}>{i + 1}</span>

                  {/* Avatar */}
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: `linear-gradient(135deg, ${m.ring}, ${m.bg})`,
                    color: m.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700, flexShrink: 0,
                  }}>{(v.name || "?")[0]}</div>

                  {/* Name + bar */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1A1A2E", marginBottom: 3 }}>{v.name}</div>
                    <div style={{ height: 4, background: "#F0F0F5", borderRadius: 2 }}>
                      <div style={{
                        height: 4, borderRadius: 2,
                        background: i === 0 ? "#C41E3A" : "#D4D4D8",
                        width: `${barW}%`,
                        transition: "width .5s ease",
                      }} />
                    </div>
                  </div>

                  {/* Hours */}
                  <span style={{
                    fontSize: 14, fontWeight: 800, color: "#C41E3A",
                    fontFamily: "Assistant", flexShrink: 0,
                  }}>{v.hours.toFixed(1)}</span>
                </div>
              );
            })}
            {topVols.length === 0 && (
              <div style={{ textAlign: "center", color: "#A3A3B5", padding: 20, fontSize: 13 }}>אין נתונים</div>
            )}
          </div>
        </div>
      </div>

      {/* ════════ Two-column: Coordinators + Recent ════════ */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: 14,
      }}>

        {/* ──── Coordinators monthly activity ──── */}
        <div style={{
          background: "#fff", borderRadius: 14,
          border: "1px solid #E8E8ED",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,.03)",
          animation: "dashFadeIn .3s ease .2s both",
        }}>
          <div style={{
            padding: "14px 18px",
            borderBottom: "1px solid #F0F0F5",
            display: "flex", alignItems: "center", gap: 8,
            background: "linear-gradient(135deg, #F8FAFC 0%, #FFF 100%)",
          }}>
            <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "Assistant", color: "#1A1A2E" }}>
              פעילות רכזים חודשי
            </span>
          </div>
          <div style={{ padding: "8px 10px" }}>
            {coordinatorLeaderboard.map((c, i) => {
              const m = medal(i);
              return (
                <div
                  key={c.id}
                  className="dash-leader-row"
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 8px", borderRadius: 8,
                  }}
                >
                  <span style={{
                    width: 24, height: 24, borderRadius: "50%",
                    background: m.bg, border: `1.5px solid ${m.ring}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, color: m.color,
                    flexShrink: 0,
                  }}>{i + 1}</span>

                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#1A1A2E" }}>
                    {c.label}
                  </div>

                  <div style={{ textAlign: "left", flexShrink: 0 }}>
                    <span style={{
                      fontFamily: "Assistant", fontWeight: 800,
                      color: "#C41E3A", fontSize: 14,
                    }}>{c.hours.toFixed(1)}</span>
                    <span style={{ fontSize: 11, color: "#8E8EA0", marginRight: 6 }}>
                      {" "}· {c.count} פע׳
                    </span>
                  </div>
                </div>
              );
            })}
            {coordinatorLeaderboard.length === 0 && (
              <div style={{ textAlign: "center", color: "#A3A3B5", padding: 20, fontSize: 13 }}>אין פעילות להצגה</div>
            )}
          </div>
        </div>

        {/* ──── Recent activity ──── */}
        <div style={{
          background: "#fff", borderRadius: 14,
          border: "1px solid #E8E8ED",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,.03)",
          animation: "dashFadeIn .3s ease .25s both",
        }}>
          <div style={{
            padding: "14px 18px",
            borderBottom: "1px solid #F0F0F5",
          }}>
            <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "Assistant", color: "#1A1A2E" }}>
              פעילות אחרונה
            </span>
          </div>

          {!isMobile ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["מתנדב", "תחום", "תאריך", "משך", "תרומות", "שווי"].map(h => (
                    <th key={h} style={{
                      padding: "10px 14px", fontSize: 11, fontWeight: 600,
                      color: "#8E8EA0", textAlign: "right",
                      background: "#FAFAFC", borderBottom: "1px solid #F0F0F5",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent.map((a, idx) => (
                  <tr key={a.id} className="dash-recent-row" style={{
                    animation: `dashFadeIn .2s ease ${0.3 + idx * 0.03}s both`,
                  }}>
                    <td style={{
                      padding: "10px 14px", fontSize: 13, fontWeight: 600,
                      borderBottom: "1px solid #F5F5F7", color: "#1A1A2E",
                    }}>{volMap[a.volunteerId]?.name || "—"}</td>
                    <td style={{ padding: "10px 14px", borderBottom: "1px solid #F5F5F7" }}>
                      {domainMap[a.domainId] ? <DomainTag domain={domainMap[a.domainId]} /> : "—"}
                    </td>
                    <td style={{
                      padding: "10px 14px", borderBottom: "1px solid #F5F5F7",
                      fontSize: 12, color: "#6B6B80", whiteSpace: "nowrap",
                    }}>{a.date} {a.time || ""}</td>
                    <td style={{
                      padding: "10px 14px", borderBottom: "1px solid #F5F5F7",
                      fontSize: 13, fontWeight: 600, color: "#2D2D3F",
                    }}>{a.duration} ש׳</td>
                    <td style={{ padding: "12px 14px", borderBottom: "1px solid #EEF2F7", color: "#9A3412", fontWeight: 700 }}>
                      {getActivityContributionTotal(a) > 0 ? getActivityContributionTotal(a) : "—"}
                    </td>
                    <td style={{ padding: "12px 14px", borderBottom: "1px solid #EEF2F7", color: "#0F766E", fontWeight: 700 }}>
                      {getActivityContributionValueTotal(a) > 0 ? formatContributionCurrency(getActivityContributionValueTotal(a)) : "—"}
                    </td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 28, color: "#A3A3B5", fontSize: 13 }}>אין פעילות עדיין</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {recent.map((a, idx) => (
                <div key={a.id} style={{
                  border: "1px solid #F0F0F5", borderRadius: 10,
                  padding: 10,
                  animation: `dashFadeIn .2s ease ${0.3 + idx * 0.03}s both`,
                }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 4,
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#1A1A2E" }}>
                      {volMap[a.volunteerId]?.name || "—"}
                    </span>
                    <span style={{ fontSize: 11, color: "#8E8EA0" }}>
                      {a.date} {a.time || ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {domainMap[a.domainId] ? <DomainTag domain={domainMap[a.domainId]} /> : "—"}
                    <span style={{ fontSize: 12, color: "#2D2D3F", fontWeight: 600 }}>{a.duration} ש׳</span>
                    {getActivityContributionTotal(a) > 0 && (
                      <span style={{ fontSize: 12, color: "#9A3412", fontWeight: 700 }}>
                        {getActivityContributionTotal(a)} תרומות
                      </span>
                    )}
                  </div>
                  {getActivityContributionTotal(a) > 0 && (
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                      {getActivityContributionSummary(a)}
                    </div>
                  )}
                  {getActivityContributionValueTotal(a) > 0 && (
                    <div style={{ fontSize: 11, color: "#0F766E", marginTop: 4, fontWeight: 700 }}>
                      שווי: {formatContributionCurrency(getActivityContributionValueTotal(a))}
                    </div>
                  )}
                </div>
              ))}
              {recent.length === 0 && (
                <div style={{ textAlign: "center", padding: 20, color: "#A3A3B5", fontSize: 13 }}>אין פעילות עדיין</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ════════ Cities Summary Table ════════ */}
      <div style={{
        marginTop: 16,
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #E8E8ED",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,.03)",
        animation: "dashFadeIn .3s ease .3s both",
      }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #F0F0F5" }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "Assistant", color: "#1A1A2E" }}>
            ערים מובילות לפי כמות מתנדבים ונעזרים
          </div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
            הכי הרבה מתנדבים: {topVolunteerCity ? `${topVolunteerCity.city} (${topVolunteerCity.volunteersCount})` : "אין נתונים"} · הכי הרבה נעזרים: {topBeneficiaryCity ? `${topBeneficiaryCity.city} (${topBeneficiaryCity.beneficiariesCount})` : "אין נתונים"}
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr>
                {["עיר", "מתנדבים", "נעזרים", "סה״כ"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", fontSize: 12, fontWeight: 600, color: "#8E8EA0", textAlign: "right", background: "#FAFAFC", borderBottom: "1px solid #F0F0F5" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {citySummaryRows.slice(0, 10).map((row) => (
                <tr key={row.city} className="dash-recent-row">
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #F5F5F7", fontSize: 13, fontWeight: 600, color: "#1A1A2E" }}>{row.city}</td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #F5F5F7", fontSize: 13, color: "#2563EB", fontWeight: 700 }}>{row.volunteersCount}</td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #F5F5F7", fontSize: 13, color: "#059669", fontWeight: 700 }}>{row.beneficiariesCount}</td>
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid #F5F5F7", fontSize: 13, color: "#2D2D3F", fontWeight: 700 }}>{row.volunteersCount + row.beneficiariesCount}</td>
                </tr>
              ))}
              {citySummaryRows.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: 24, color: "#A3A3B5", fontSize: 13 }}>אין נתוני ערים להצגה</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
